import { api, ApiError, type SharedFolderGateResponse } from '../api/client';
import { uploadErrorMessageKey } from '../api/uploadErrors';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import {
  startGuestAccessExpiryMonitor,
  type GuestAccessExpiryHandle,
} from '../app/guestAccessExpiry';
import { guestVerifyThrottleErrorKey } from '../app/guestVerifyThrottle';
import { logoutGuest } from '../app/index';
import {
  decryptFolderGuestEnvelope,
  resolveGuestWrapKey,
} from '../crypto/sharedFolderGuestInvite';
import {
  deriveProofKeys,
  signGateChallenge,
} from '../crypto/infoShareFragment';
import { decodeBase64Url } from '../crypto/primitives';
import {
  decryptFolderManifest,
  isDirectoryEmpty,
  isManifestPathTaken,
  joinManifestPath,
  listFolderContents,
  parentManifestPath,
  type SharedFolderActorRef,
  type SharedFolderDirectoryEntry,
  type SharedFolderFileEntry,
  type SharedFolderManifestPlaintext,
} from '../crypto/sharedFolderManifest';
import { resolveSharedFolderGuestFragment } from '../crypto/sharedFolderGuestFragment';
import {
  clampPathToGuestRoot,
  isPathWithinGuestRoot,
  normalizeGuestRootPath,
} from '../crypto/sharedFolderGuestRoot';
import {
  formatGuestUploadBudget,
  guestCanUpload,
  replaceSharedFolderFileContentsAsGuest,
  uploadFileToSharedFolderAsGuest,
} from '../crypto/sharedFolderGuestFile';
import {
  createSharedFolderGuestSubfolder,
  deleteSharedFolderGuestFile,
  deleteSharedFolderGuestSubfolder,
  moveSharedFolderGuestFile,
  moveSharedFolderGuestSubfolder,
  renameSharedFolderGuestFile,
  renameSharedFolderGuestSubfolder,
  summarizeGuestDirectoryDelete,
} from '../crypto/sharedFolderGuestSubfolder';
import { downloadSharedFolderFile, formatFileSize, triggerBrowserDownload } from '../crypto/sharedFolderFile';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { enhanceDataTableCards } from '../ui/dataTableCards';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { showConfirmDialog } from '../ui/confirmDialog';
import { closeFilesContextMenu, openFilesContextMenu } from '../ui/filesContextMenu';
import { renderFilesBreadcrumb } from '../ui/filesBreadcrumb';
import { applyUploadProgress, hideUploadProgress } from '../ui/uploadProgress';
import {
  bindFilesConflictBanner,
  filesConflictBannerMarkup,
  formatFilesConflictStatus,
  hideFilesConflictBanner,
  showFilesConflictBanner,
} from '../ui/filesConflictBanner';
import {
  bindFilesBrowseMobileTree,
  FILES_MOBILE_LAYOUT_MQ,
  type FilesBrowseMobileTreeHandle,
} from '../ui/filesBrowseMobile';
import { ensureExpandedAncestors, renderFilesDirectoryTree } from '../ui/filesDirectoryTree';
import {
  collectManifestNameMatches,
  expandFoldersForSearchHits,
  fillHighlightedText,
  mountFilesTreeSearchBar,
  pathAncestorKeys,
  stepSearchIndex,
  toSearchState,
  type FilesTreeSearchMatch,
} from '../ui/filesTreeSearch';
import { bindBrowseSplitResize } from '../ui/splitPaneResize';
import { bindFilesDropUpload, isExternalFileDrag } from '../ui/filesDropUpload';
import { showFilesMoveDialog } from '../ui/filesMoveDialog';
import { showFilesPropertiesDialog } from '../ui/filesPropertiesDialog';
import { invalidSharedFolderEntryName, showInputDialog, throwSharedFolderInputError } from '../ui/inputDialog';
import { iconImg } from '../ui/icons';
import { createHelpTip } from '../help/helpTips';
import { connectSharedFolderLive, disconnectSharedFolderLive } from '../ws/sharedFolderLive';
import { showFileTextEditor, type FileTextEditorHandle } from '../ui/fileTextEditorDialog';
import { canEditPlainText, ensureTextFileExtension, fileTypeIconFor, fileTypeLabelKeyFor, inferContentTypeFromFileName } from '../files/fileTypes';
import { fileTypeImg } from '../ui/fileTypeImg';

function parseGuestRoute(): { folderToken: string; guestInviteId: string } | null {
  const match = window.location.pathname.match(/^\/f\/([^/]+)\/g\/([^/]+)$/);
  return match ? { folderToken: match[1], guestInviteId: match[2] } : null;
}

function entryDisplayName(path: string, currentPath: string): string {
  const prefix = currentPath ? `${currentPath}/` : '';
  return path.startsWith(prefix) ? path.slice(prefix.length) : path;
}

function entryNameConflict(
  manifest: SharedFolderManifestPlaintext,
  parentPath: string,
  newName: string,
  ignorePath?: string,
): string | null {
  const invalid = invalidSharedFolderEntryName(newName);
  if (invalid) {
    return invalid;
  }
  if (isManifestPathTaken(manifest, joinManifestPath(parentPath, newName), ignorePath)) {
    return t('files.owner.nameInUse');
  }
  return null;
}

function tGuestPermission(permission: string): string {
  switch (permission) {
    case 'ReadWrite':
      return t('files.owner.accessReadWrite');
    case 'Read':
      return t('files.owner.accessRead');
    default:
      return permission;
  }
}

export function GuestSharedFolderPage(): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'center-panel guest-panel';

  const route = parseGuestRoute();
  if (!route) {
    panel.innerHTML = `<p class="error-text" data-i18n="files.guest.invalidLink"></p>`;
    applyI18n(panel);
    return panel;
  }

  const fragmentKey = `${route.folderToken}:${route.guestInviteId}`;
  const fragment =
    resolveSharedFolderGuestFragment(fragmentKey)
    ?? resolveSharedFolderGuestFragment(route.folderToken);
  if (!fragment) {
    panel.innerHTML = `
      <h1 data-i18n="files.guest.missingFragmentTitle"></h1>
      <p class="muted" data-i18n="files.guest.missingFragment"></p>
    `;
    applyI18n(panel);
    return panel;
  }

  const proofSeed = decodeBase64Url(fragment.fs);
  const { secretKey: proofSecretKey } = deriveProofKeys(proofSeed);
  const passwordProtected = Boolean(fragment.salt);

  panel.innerHTML = `
    <h1 class="panel-title" id="files-guest-title">
      <span class="panel-title__icon"></span>
      <span data-i18n="files.guest.title"></span>
    </h1>
    <p class="muted" id="files-guest-subtitle" data-i18n="files.guest.subtitle"></p>
    <label class="guest-password-field" id="files-guest-password-field" hidden>
      <span data-i18n="info.guest.passwordLabel"></span>
      <input type="password" id="files-guest-password" autocomplete="off" />
    </label>
    <div class="guest-actions" id="files-guest-actions">
      <button type="button" class="button primary" id="files-guest-open" hidden>
        <span data-i18n="files.guest.open"></span>
      </button>
    </div>
    <div id="files-guest-busy-host"></div>
    <div id="files-guest-content" hidden></div>
    <p class="error-text" id="files-guest-error" hidden></p>
    <p class="muted" id="files-guest-status" hidden></p>
  `;

  panel.querySelector('.panel-title__icon')?.append(iconImg('files', 28));
  panel.querySelector('#files-guest-title')?.append(createHelpTip('guest-files'));
  applyI18n(panel);

  const passwordField = panel.querySelector('#files-guest-password-field') as HTMLLabelElement;
  const passwordInput = panel.querySelector('#files-guest-password') as HTMLInputElement;
  const openButton = panel.querySelector('#files-guest-open') as HTMLButtonElement;
  const busyHost = panel.querySelector('#files-guest-busy-host') as HTMLDivElement;
  const contentEl = panel.querySelector('#files-guest-content') as HTMLDivElement;
  const errorEl = panel.querySelector('#files-guest-error') as HTMLParagraphElement;
  const statusEl = panel.querySelector('#files-guest-status') as HTMLParagraphElement;
  const gateTitleEl = panel.querySelector('#files-guest-title') as HTMLElement;
  const gateSubtitleEl = panel.querySelector('#files-guest-subtitle') as HTMLElement;
  const gateActionsEl = panel.querySelector('#files-guest-actions') as HTMLElement;

  const syncGuestGateChrome = () => {
    const hideGate = opened;
    gateTitleEl.hidden = hideGate;
    gateSubtitleEl.hidden = hideGate;
    gateActionsEl.hidden = hideGate;
  };

  let gateResponseText = '';
  let gateInfo: SharedFolderGateResponse | null = null;
  let folderId: string | null = null;
  let folderKey: Uint8Array | null = null;
  let currentManifest: SharedFolderManifestPlaintext = { entries: [] };
  let manifestRevision = 0;
  let syncingManifest = false;
  let passwordRequired = passwordProtected;
  let opening = false;
  let opened = false;
  let accessEnded = false;
  let disposed = false;
  let mountGeneration = 0;
  let errorKey: string | null = null;
  let statusKey: string | null = null;
  let uploading = false;
  let downloading = false;
  let pendingConflictMessage = '';
  const guestRootPath = normalizeGuestRootPath(fragment.rp);
  let currentPath = guestRootPath;
  let busy = false;
  let openTextEditor: FileTextEditorHandle | null = null;
  const expandedPaths = new Set<string>();
  let searchQuery = '';
  let searchMatches: FilesTreeSearchMatch[] = [];
  let searchIndex = -1;
  let searchHitEntryKey: string | null = null;
  let disposeBrowseSplit: (() => void) | null = null;
  let disposeMobileTree: (() => void) | null = null;
  let mobileTree: FilesBrowseMobileTreeHandle | null = null;
  let expiryMonitor: GuestAccessExpiryHandle | null = null;
  let expiryWarningEl: HTMLElement | null = null;

  const refreshGuestGate = async (): Promise<{
    gateResponseText: string;
    gateInfo: SharedFolderGateResponse;
  }> => {
    const text = await api.getSharedFolderGate(route.folderToken, route.guestInviteId);
    return {
      gateResponseText: text,
      gateInfo: JSON.parse(text) as SharedFolderGateResponse,
    };
  };

  const showExpiryWarning = (remainingMs: number) => {
    const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
    if (!expiryWarningEl) {
      expiryWarningEl = document.createElement('div');
      expiryWarningEl.className = 'warning-panel';
      expiryWarningEl.id = 'files-guest-expiry-warning';
      const icon = document.createElement('span');
      icon.className = 'warning-panel__icon';
      icon.append(iconImg('warning', 20));
      const text = document.createElement('span');
      text.id = 'files-guest-expiry-warning-text';
      expiryWarningEl.append(icon, text);
    }
    const textEl = expiryWarningEl.querySelector('#files-guest-expiry-warning-text') as HTMLElement;
    textEl.textContent = t('files.guest.expiresSoon', { minutes });
    const chrome = contentEl.querySelector('.files-browse-chrome');
    if (chrome) {
      chrome.after(expiryWarningEl);
    } else if (!expiryWarningEl.isConnected) {
      contentEl.prepend(expiryWarningEl);
    }
  };

  const clearExpiryWarning = () => {
    expiryWarningEl?.remove();
  };

  const showTransientGateWarning = (code: string) => {
    errorKey = code === 'verify_locked_out'
      ? 'errors.verifyLockedOut'
      : 'errors.rateLimited';
    errorEl.hidden = false;
    errorEl.textContent = t(errorKey);
  };

  const clearTransientGateWarning = () => {
    if (errorKey !== 'errors.rateLimited' && errorKey !== 'errors.verifyLockedOut') {
      return;
    }
    errorKey = null;
    errorEl.hidden = true;
    errorEl.textContent = '';
  };

  const endGuestAccess = async (messageKey: string) => {
    if (disposed || accessEnded) {
      return;
    }
    accessEnded = true;

    disconnectSharedFolderLive();
    expiryMonitor?.dispose();
    expiryMonitor = null;
    clearExpiryWarning();
    openTextEditor?.close();
    openTextEditor = null;

    if (opened) {
      await logoutGuest();
    }
    if (!panel.isConnected || disposed) {
      return;
    }

    opened = false;
    contentEl.hidden = true;
    contentEl.replaceChildren();
    passwordField.hidden = true;
    openButton.disabled = true;
    syncGuestGateChrome();
    gateActionsEl.hidden = true;
    errorEl.hidden = true;
    statusKey = messageKey;
    statusEl.hidden = false;
    statusEl.textContent = t(statusKey);
  };

  const startExpiryMonitor = (expiresAt: string | null | undefined) => {
    expiryMonitor?.dispose();
    expiryMonitor = null;
    clearExpiryWarning();
    if (!expiresAt) return;
    expiryMonitor = startGuestAccessExpiryMonitor({
      expiresAt,
      refreshExpiry: async () => {
        const refreshed = await refreshGuestGate();
        gateInfo = refreshed.gateInfo;
        clearTransientGateWarning();
        return {
          expired: refreshed.gateInfo.expired,
          revoked: refreshed.gateInfo.revoked,
          expiresAt: refreshed.gateInfo.expiresAt,
        };
      },
      onWarning: showExpiryWarning,
      onWarningClear: clearExpiryWarning,
      onTransientRefreshError: showTransientGateWarning,
      onExpiredLogout: async () => {
        await endGuestAccess(
          gateInfo?.revoked ? 'files.guest.accessRevoked' : 'files.guest.expired',
        );
      },
    });
  };

  const canModify = (permission: string) => permission === 'ReadWrite';

  const getBudgetState = () => ({
    maxGuestUploadFiles: gateInfo?.maxGuestUploadFiles ?? null,
    maxGuestUploadTotalBytes: gateInfo?.maxGuestUploadTotalBytes ?? null,
    guestUploadFilesUsed: gateInfo?.guestUploadFilesUsed ?? 0,
    guestUploadBytesUsed: gateInfo?.guestUploadBytesUsed ?? 0,
  });

  const updateBudgetFromCommit = (filesUsed?: number, bytesUsed?: number) => {
    if (!gateInfo) {
      return;
    }

    if (filesUsed !== undefined) {
      gateInfo = { ...gateInfo, guestUploadFilesUsed: filesUsed };
    }
    if (bytesUsed !== undefined) {
      gateInfo = { ...gateInfo, guestUploadBytesUsed: bytesUsed };
    }
  };

  const refreshLocale = () => {
    if (errorKey && !errorEl.hidden) {
      errorEl.textContent = t(errorKey);
    }
    if (statusKey && !statusEl.hidden) {
      statusEl.textContent = t(statusKey);
    }
    if (gateInfo && panel.querySelector('.files-browse-split')) {
      renderFolderContents(gateInfo.permission, currentManifest);
    }
  };

  const unbindLocale = bindLocaleRefresh(refreshLocale);
  const observer = new MutationObserver(() => {
    if (!panel.isConnected) {
      disposed = true;
      mountGeneration += 1;
      openTextEditor?.close();
      openTextEditor = null;
      disconnectSharedFolderLive();
      disposeBrowseSplit?.();
      disposeBrowseSplit = null;
      disposeMobileTree?.();
      disposeMobileTree = null;
      mobileTree = null;
      expiryMonitor?.dispose();
      expiryMonitor = null;
      clearExpiryWarning();
      unbindLocale();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const showError = (key: string) => {
    errorKey = key;
    errorEl.hidden = false;
    errorEl.textContent = t(key);
    statusEl.hidden = true;
  };

  const setCurrentPath = (path: string) => {
    mobileTree?.closeTree();
    currentPath = clampPathToGuestRoot(path, guestRootPath);
    ensureExpandedAncestors(currentPath, expandedPaths, guestRootPath);
  };

  const scopedManifest = (manifest: SharedFolderManifestPlaintext): SharedFolderManifestPlaintext => {
    if (!guestRootPath) {
      return manifest;
    }

    return {
      entries: manifest.entries.filter((entry) => isPathWithinGuestRoot(entry.path, guestRootPath)),
    };
  };

  const guestFileMime = (file: SharedFolderFileEntry): string =>
    file.contentType
    || inferContentTypeFromFileName(entryDisplayName(file.path, currentPath))
    || 'application/octet-stream';

  const canEditGuestFile = (file: SharedFolderFileEntry, permission: string): boolean =>
    canModify(permission)
    && canEditPlainText(entryDisplayName(file.path, currentPath), guestFileMime(file), file.size);

  const openGuestTextEditor = (file: SharedFolderFileEntry, text: string) => {
    if (!folderKey) {
      return;
    }

    openTextEditor?.close();
    openTextEditor = showFileTextEditor({
      fileName: entryDisplayName(file.path, currentPath),
      fileId: file.fileId,
      initialText: text,
      entry: file,
      save: async (nextText) => {
        if (!folderKey) {
          throw new Error('status.error');
        }
        const result = await replaceSharedFolderFileContentsAsGuest({
          folderToken: route.folderToken,
          guestInviteId: route.guestInviteId,
          folderKey,
          revision: manifestRevision,
          manifest: currentManifest,
          fileId: file.fileId,
          file: new Blob([nextText], { type: guestFileMime(file) }),
          actor: {
            kind: 'guest',
            id: route.guestInviteId,
            displayName: t('admin.audit.guestActor'),
          },
        });
        manifestRevision = result.revision;
        currentManifest = result.manifest;
        updateBudgetFromCommit(result.guestUploadFilesUsed, result.guestUploadBytesUsed);
        return result.entry;
      },
      onClose: () => {
        openTextEditor = null;
      },
    });
  };

  const syncOpenTextEditor = async () => {
    const handle = openTextEditor;
    if (!handle?.isOpen() || !folderKey) {
      return;
    }

    const entry = currentManifest.entries.find(
      (item): item is SharedFolderFileEntry => item.type === 'file' && item.fileId === handle.fileId,
    );
    if (!entry) {
      handle.markDeleted();
      return;
    }
    if (!handle.needsRemote(entry)) {
      return;
    }

    const bytes = await downloadSharedFolderFile({
      folderKey,
      entry,
      guestRoute: { folderToken: route.folderToken, guestInviteId: route.guestInviteId },
    });
    handle.applyRemote(new TextDecoder().decode(bytes), entry);
  };

  const denyExternalFileDrop = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'none';
    }
  };
  panel.addEventListener('dragover', denyExternalFileDrop, true);
  panel.addEventListener('drop', denyExternalFileDrop, true);

  const clearConflict = () => {
    pendingConflictMessage = '';
    hideFilesConflictBanner(contentEl.querySelector('#files-guest-conflict') as HTMLElement | null);
  };

  const showConflict = (message: string) => {
    pendingConflictMessage = message;
    const conflictEl = contentEl.querySelector('#files-guest-conflict') as HTMLElement | null;
    if (!conflictEl) {
      return;
    }
    bindFilesConflictBanner(conflictEl, clearConflict);
    showFilesConflictBanner(conflictEl, message);
  };

  const noteConflicts = (conflicts: Array<{ originalPath: string; resolvedPath: string }>) => {
    const message = formatFilesConflictStatus(conflicts);
    if (message) {
      showConflict(message);
    }
  };

  const uploadGuestFiles = async (files: File[], targetPath: string) => {
    if (!folderKey || !gateInfo || uploading || files.length === 0) {
      return;
    }

    uploading = true;
    errorEl.hidden = true;
    errorKey = null;
    clearConflict();
    const uploadStatusEl = contentEl.querySelector('#files-guest-upload-status') as HTMLParagraphElement | null;
    const uploadProgressEl = contentEl.querySelector('#files-guest-upload-progress') as HTMLProgressElement | null;
    if (uploadStatusEl) {
      uploadStatusEl.hidden = false;
      uploadStatusEl.textContent = t('files.owner.uploading');
    }
    if (uploadProgressEl) {
      uploadProgressEl.hidden = true;
    }

    try {
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
      let completedBytes = 0;
      let conflictMessage = '';
      for (const file of files) {
        if (!folderKey || !gateInfo) {
          return;
        }
        const result = await uploadFileToSharedFolderAsGuest({
          folderToken: route.folderToken,
          guestInviteId: route.guestInviteId,
          folderKey,
          revision: manifestRevision,
          manifest: currentManifest,
          currentPath: targetPath,
          file,
          actor: {
            kind: 'guest',
            id: route.guestInviteId,
            displayName: t('admin.audit.guestActor'),
          },
          onProgress: (progress) => {
            const status = contentEl.querySelector('#files-guest-upload-status') as HTMLParagraphElement | null;
            const progressEl = contentEl.querySelector('#files-guest-upload-progress') as HTMLProgressElement | null;
            if (!status) {
              return;
            }
            applyUploadProgress(status, progressEl, {
              ...progress,
              bytesCompleted: completedBytes + progress.bytesCompleted,
              bytesTotal: totalBytes,
            });
          },
        });
        completedBytes += file.size;
        manifestRevision = result.revision;
        currentManifest = result.manifest;
        updateBudgetFromCommit(result.guestUploadFilesUsed, result.guestUploadBytesUsed);
        const message = formatFilesConflictStatus(result.conflicts);
        if (message) {
          conflictMessage = message;
        }
      }
      if (conflictMessage) {
        pendingConflictMessage = conflictMessage;
      }
      renderFolderContents(gateInfo.permission, currentManifest);
    } catch (cause) {
      console.error('Guest upload failed', cause);
      if (gateInfo) {
        renderFolderContents(gateInfo.permission, currentManifest);
      }
      showError(uploadErrorMessageKey(cause, 'files.guest.uploadFailed'));
    } finally {
      uploading = false;
      hideUploadProgress(
        contentEl.querySelector('#files-guest-upload-status') as HTMLParagraphElement | null,
        contentEl.querySelector('#files-guest-upload-progress') as HTMLProgressElement | null,
      );
    }
  };

  const renderFolderContents = (permission: string, manifest: SharedFolderManifestPlaintext) => {
    closeFilesContextMenu();
    setCurrentPath(currentPath);
    const viewManifest = scopedManifest(manifest);
    const { directories, files } = listFolderContents(viewManifest, currentPath);
    const budget = formatGuestUploadBudget(getBudgetState());
    const canUpload = guestCanUpload({ permission, ...getBudgetState() });
    const modify = canModify(permission);
    const compactActions = window.matchMedia(FILES_MOBILE_LAYOUT_MQ).matches;
    const hasCreateActions = modify || canUpload;
    let actionsMarkup = '';
    if (hasCreateActions && compactActions) {
      actionsMarkup = `
        <button type="button" class="icon-button files-explorer-add" id="files-guest-add" data-i18n-aria-label="files.owner.addActions" data-i18n-title="files.owner.addActions" aria-haspopup="menu"></button>
        ${modify ? '<button type="button" id="files-guest-new-folder" hidden></button>' : ''}
        ${canUpload ? '<button type="button" id="files-guest-new-text" hidden></button>' : ''}
        ${canUpload ? '<input type="file" id="files-guest-upload" multiple hidden />' : ''}
      `;
    } else {
      const uploadMarkup = canUpload
        ? `<label class="button primary files-folder-panel__upload">
            <span data-i18n="files.guest.upload"></span>
            <input type="file" id="files-guest-upload" multiple hidden />
          </label>`
        : '';
      const newFolderMarkup = modify
        ? `<button type="button" class="button ghost" id="files-guest-new-folder">
            <span data-i18n="files.owner.newFolder"></span>
          </button>`
        : '';
      const newTextMarkup = canUpload
        ? `<button type="button" class="button ghost" id="files-guest-new-text">
            <span data-i18n="files.owner.newTextFile"></span>
          </button>`
        : '';
      actionsMarkup = `${newFolderMarkup}${newTextMarkup}${uploadMarkup}`;
    }

    contentEl.hidden = false;
    disposeBrowseSplit?.();
    disposeBrowseSplit = null;
    disposeMobileTree?.();
    disposeMobileTree = null;
    mobileTree = null;
    contentEl.innerHTML = `
      <div class="panel files-folder-panel files-browse-main files-browse-main--guest">
        <div class="files-browse-chrome files-browse-chrome--guest">
          <p class="files-browse-chrome__meta">
            <span class="files-browse-chrome__meta-item">${t('files.guest.accessLabel')}: ${tGuestPermission(permission)}</span>${budget ? `<span class="files-browse-chrome__meta-item">${t('files.guest.uploadBudgetLabel')}: ${budget}</span>` : ''}${syncingManifest ? `<span class="files-browse-chrome__meta-item">${t('files.owner.syncingManifest')}</span>` : ''}
          </p>
        </div>
        <div class="files-browse-split">
          <div class="files-browse-tree-pane">
            <div id="files-guest-tree-search"></div>
            <nav id="files-guest-tree"></nav>
          </div>
          <section class="files-browse-files">
            <div class="files-explorer-bar">
              <nav class="files-folder-breadcrumb" id="files-guest-breadcrumb"></nav>
              <div class="files-explorer-bar__actions">
                ${actionsMarkup}
              </div>
            </div>
            <div class="files-table-wrap files-browse-files__table-wrap">
              <table class="data-table files-table">
                <thead>
                  <tr>
                    <th data-i18n="files.owner.fileName"></th>
                    <th data-i18n="files.owner.propertiesType"></th>
                    <th data-i18n="files.owner.fileSize"></th>
                    <th class="data-table__actions-heading" data-i18n="files.owner.actions"></th>
                  </tr>
                </thead>
                <tbody id="files-guest-table-body"></tbody>
              </table>
              <p class="muted files-browse-empty" id="files-guest-empty" hidden data-i18n="files.owner.emptyFolder"></p>
            </div>
            <p class="status-text" id="files-guest-upload-status" hidden></p>
            <progress class="files-upload-progress" id="files-guest-upload-progress" hidden></progress>
            ${filesConflictBannerMarkup('files-guest-conflict')}
          </section>
        </div>
      </div>
    `;
    applyI18n(contentEl);
    contentEl.querySelector('#files-guest-add')?.append(iconImg('plus', 16));

    const guestSplit = contentEl.querySelector('.files-browse-split') as HTMLElement | null;
    const guestExplorerBar = contentEl.querySelector('.files-explorer-bar') as HTMLElement | null;
    disposeBrowseSplit = guestSplit ? bindBrowseSplitResize(guestSplit) : null;
    if (guestSplit && guestExplorerBar) {
      mobileTree = bindFilesBrowseMobileTree({ root: guestSplit, explorerBar: guestExplorerBar });
      disposeMobileTree = () => mobileTree?.dispose();
    } else {
      disposeMobileTree = null;
    }

    searchMatches = collectManifestNameMatches(searchQuery, viewManifest);
    if (searchHitEntryKey) {
      searchIndex = searchMatches.findIndex((m) => m.entryKey === searchHitEntryKey);
      if (searchIndex < 0) searchHitEntryKey = null;
    }
    const searchState = toSearchState(searchQuery, searchMatches);
    if (searchMatches.length > 0) {
      expandFoldersForSearchHits(searchMatches, (key) => expandedPaths.add(key), pathAncestorKeys);
    }

    const stepSearch = (direction: -1 | 1) => {
      if (searchMatches.length === 0) return;
      searchIndex = stepSearchIndex(searchIndex, direction, searchMatches.length);
      const match = searchMatches[searchIndex];
      if (!match) return;
      searchHitEntryKey = match.entryKey;
      setCurrentPath(match.parentKey);
      renderFolderContents(permission, currentManifest);
    };

    const searchHost = contentEl.querySelector('#files-guest-tree-search') as HTMLElement | null;
    if (searchHost) {
      const bar = mountFilesTreeSearchBar(searchHost, {
        placeholderKey: 'files.search.placeholder',
        initialQuery: searchQuery,
        onQueryChange: (query) => {
          searchQuery = query;
          searchIndex = -1;
          searchHitEntryKey = null;
          renderFolderContents(permission, currentManifest);
        },
        onStep: stepSearch,
      });
      bar.setResultCount(searchMatches.length);
    }

    renderFilesBreadcrumb(contentEl.querySelector('#files-guest-breadcrumb') as HTMLElement, currentPath, (path) => {
      setCurrentPath(path);
      renderFolderContents(permission, currentManifest);
    }, { rootPath: guestRootPath });
    renderFilesDirectoryTree(contentEl.querySelector('#files-guest-tree') as HTMLElement, viewManifest, {
      currentPath,
      expandedPaths,
      rootPath: guestRootPath || undefined,
      ariaLabelKey: 'files.owner.directoryTree',
      search: searchState,
      onSelect: (path) => {
        setCurrentPath(path);
        renderFolderContents(permission, currentManifest);
      },
      onToggle: (path, expanded) => {
        if (expanded) expandedPaths.add(path);
        else expandedPaths.delete(path);
        renderFolderContents(permission, currentManifest);
      },
    });

    const tbody = contentEl.querySelector('#files-guest-table-body') as HTMLTableSectionElement;
    const emptyEl = contentEl.querySelector('#files-guest-empty') as HTMLParagraphElement;

    const guestActor: SharedFolderActorRef = {
      kind: 'guest',
      id: route.guestInviteId,
      displayName: t('admin.audit.guestActor'),
    };

    const editGuestFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || downloading) {
        return;
      }
      downloading = true;
      errorEl.hidden = true;
      statusKey = 'files.guest.downloading';
      statusEl.hidden = false;
      statusEl.textContent = t(statusKey);
      try {
        const bytes = await downloadSharedFolderFile({
          folderKey,
          entry: file,
          guestRoute: {
            folderToken: route.folderToken,
            guestInviteId: route.guestInviteId,
          },
        });
        statusEl.hidden = true;
        downloading = false;
        openGuestTextEditor(file, new TextDecoder().decode(bytes));
      } catch (cause) {
        console.error('Guest file edit failed', cause);
        showError('files.guest.downloadFailed');
        downloading = false;
      }
    };

    const downloadGuestFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || downloading) {
        return;
      }
      downloading = true;
      errorEl.hidden = true;
      statusKey = 'files.guest.downloading';
      statusEl.hidden = false;
      statusEl.textContent = t(statusKey);
      try {
        const bytes = await downloadSharedFolderFile({
          folderKey,
          entry: file,
          guestRoute: {
            folderToken: route.folderToken,
            guestInviteId: route.guestInviteId,
          },
        });
        triggerBrowserDownload(entryDisplayName(file.path, currentPath), bytes);
        statusEl.hidden = true;
      } catch (cause) {
        console.error('Guest download failed', cause);
        showError('files.guest.downloadFailed');
      } finally {
        downloading = false;
      }
    };

    const openDirectoryMenu = (directory: SharedFolderDirectoryEntry, anchorRect: DOMRect) => {
      openFilesContextMenu({
        anchorRect,
        items: [
          { id: 'open', labelKey: 'files.owner.open', action: () => { setCurrentPath(directory.path); renderFolderContents(permission, currentManifest); } },
          { id: 'rename', labelKey: 'files.owner.rename', disabled: !modify, action: () => { void renameDirectory(directory); } },
          { id: 'move', labelKey: 'files.owner.moveTo', disabled: !modify, action: () => { void moveDirectory(directory); } },
          { id: 'delete', labelKey: 'files.owner.delete', disabled: !modify, action: () => { void deleteDirectory(directory); } },
          { id: 'properties', labelKey: 'files.owner.properties', action: () => showFilesPropertiesDialog({ entry: directory, currentPath }) },
        ],
      });
    };

    const openFileMenu = (file: SharedFolderFileEntry, anchorRect: DOMRect) => {
      openFilesContextMenu({
        anchorRect,
        items: [
          { id: 'download', labelKey: 'files.owner.download', action: () => { void downloadGuestFile(file); } },
          { id: 'edit', labelKey: 'files.owner.edit', hidden: !canEditGuestFile(file, permission), action: () => { void editGuestFile(file); } },
          { id: 'rename', labelKey: 'files.owner.rename', disabled: !modify, action: () => { void renameFile(file); } },
          { id: 'move', labelKey: 'files.owner.moveTo', disabled: !modify, action: () => { void moveFile(file); } },
          { id: 'delete', labelKey: 'files.owner.delete', disabled: !modify, action: () => { void deleteFile(file); } },
          { id: 'properties', labelKey: 'files.owner.properties', action: () => showFilesPropertiesDialog({ entry: file, currentPath }) },
        ],
      });
    };

    const renameDirectory = async (directory: SharedFolderDirectoryEntry) => {
      if (!folderKey || busy) return;
      await showInputDialog({
        titleKey: 'files.owner.renameFolderTitle',
        labelKey: 'files.owner.renameFolderLabel',
        defaultValue: entryDisplayName(directory.path, currentPath),
        maxLength: 200,
        validate: (newName) => entryNameConflict(currentManifest, parentManifestPath(directory.path), newName, directory.path),
        submit: async (newName) => {
          if (!folderKey || !gateInfo) {
            throwSharedFolderInputError(new Error('status.error'));
          }
          busy = true;
          try {
            const result = await renameSharedFolderGuestSubfolder({
              folderToken: route.folderToken,
              guestInviteId: route.guestInviteId,
              folderKey,
              revision: manifestRevision,
              manifest: currentManifest,
              directoryPath: directory.path,
              newName,
            });
            manifestRevision = result.revision;
            currentManifest = result.manifest;
            clearConflict();
            noteConflicts(result.conflicts);
            renderFolderContents(gateInfo.permission, result.manifest);
          } catch (cause) {
            throwSharedFolderInputError(cause);
          } finally {
            busy = false;
          }
        },
      });
    };

    const deleteDirectory = async (directory: SharedFolderDirectoryEntry) => {
      if (!folderKey || busy || !gateInfo) return;
      const recursive = !isDirectoryEmpty(currentManifest, directory.path);
      const summary = summarizeGuestDirectoryDelete(currentManifest, directory.path);
      const confirmed = recursive
        ? await showConfirmDialog({
            titleKey: 'files.owner.deleteFolderTitle',
            message: t('files.owner.deleteFolderRecursiveConfirm', { files: summary.files, folders: summary.directories }),
            confirmLabelKey: 'files.owner.delete',
          })
        : await showConfirmDialog({
            titleKey: 'files.owner.deleteFolderTitle',
            messageKey: 'files.owner.deleteFolderConfirm',
            confirmLabelKey: 'files.owner.delete',
          });
      if (!confirmed) return;
      busy = true;
      try {
        const result = await deleteSharedFolderGuestSubfolder({
          folderToken: route.folderToken,
          guestInviteId: route.guestInviteId,
          folderKey,
          revision: manifestRevision,
          manifest: currentManifest,
          directoryPath: directory.path,
          recursive,
        });
        manifestRevision = result.revision;
        currentManifest = result.manifest;
        if (currentPath === directory.path || currentPath.startsWith(`${directory.path}/`)) {
          setCurrentPath(guestRootPath);
        }
        renderFolderContents(gateInfo.permission, result.manifest);
      } catch (cause) {
        showError(apiErrorMessage(cause));
      } finally {
        busy = false;
      }
    };

    const moveDirectory = async (directory: SharedFolderDirectoryEntry) => {
      if (!folderKey || busy || !gateInfo) return;
      const destination = await showFilesMoveDialog({
        manifest: scopedManifest(currentManifest),
        currentPath,
        excludePath: directory.path,
      });
      if (destination === null) return;
      const targetParent = clampPathToGuestRoot(destination, guestRootPath);
      busy = true;
      try {
        const result = await moveSharedFolderGuestSubfolder({
          folderToken: route.folderToken,
          guestInviteId: route.guestInviteId,
          folderKey,
          revision: manifestRevision,
          manifest: currentManifest,
          directoryPath: directory.path,
          destinationDirectoryPath: targetParent,
        });
        manifestRevision = result.revision;
        currentManifest = result.manifest;
        clearConflict();
        noteConflicts(result.conflicts);
        renderFolderContents(gateInfo.permission, result.manifest);
      } catch (cause) {
        showError(apiErrorMessage(cause));
      } finally {
        busy = false;
      }
    };

    const renameFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || busy || !gateInfo) return;
      await showInputDialog({
        titleKey: 'files.owner.renameFileTitle',
        labelKey: 'files.owner.renameFileLabel',
        defaultValue: entryDisplayName(file.path, currentPath),
        maxLength: 200,
        validate: (newName) => entryNameConflict(currentManifest, parentManifestPath(file.path), newName, file.path),
        submit: async (newName) => {
          if (!folderKey || !gateInfo) {
            throwSharedFolderInputError(new Error('status.error'));
          }
          busy = true;
          try {
            const result = await renameSharedFolderGuestFile({
              folderToken: route.folderToken,
              guestInviteId: route.guestInviteId,
              folderKey,
              revision: manifestRevision,
              manifest: currentManifest,
              filePath: file.path,
              newName,
            });
            manifestRevision = result.revision;
            currentManifest = result.manifest;
            clearConflict();
            noteConflicts(result.conflicts);
            renderFolderContents(gateInfo.permission, result.manifest);
          } catch (cause) {
            throwSharedFolderInputError(cause);
          } finally {
            busy = false;
          }
        },
      });
    };

    const deleteFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || busy || !gateInfo) return;
      const confirmed = await showConfirmDialog({
        titleKey: 'files.owner.deleteFileTitle',
        messageKey: 'files.guest.deleteFileConfirm',
        confirmLabelKey: 'files.owner.delete',
      });
      if (!confirmed) return;
      busy = true;
      try {
        const result = await deleteSharedFolderGuestFile({
          folderToken: route.folderToken,
          guestInviteId: route.guestInviteId,
          folderKey,
          revision: manifestRevision,
          manifest: currentManifest,
          filePath: file.path,
        });
        manifestRevision = result.revision;
        currentManifest = result.manifest;
        renderFolderContents(gateInfo.permission, result.manifest);
      } catch (cause) {
        showError(apiErrorMessage(cause));
      } finally {
        busy = false;
      }
    };

    const moveFile = async (file: SharedFolderFileEntry) => {
      if (!folderKey || busy || !gateInfo) return;
      const destination = await showFilesMoveDialog({
        manifest: scopedManifest(currentManifest),
        currentPath,
      });
      if (destination === null) return;
      const targetParent = clampPathToGuestRoot(destination, guestRootPath);
      busy = true;
      try {
        const result = await moveSharedFolderGuestFile({
          folderToken: route.folderToken,
          guestInviteId: route.guestInviteId,
          folderKey,
          revision: manifestRevision,
          manifest: currentManifest,
          filePath: file.path,
          destinationDirectoryPath: targetParent,
        });
        manifestRevision = result.revision;
        currentManifest = result.manifest;
        clearConflict();
        noteConflicts(result.conflicts);
        renderFolderContents(gateInfo.permission, result.manifest);
      } catch (cause) {
        showError(apiErrorMessage(cause));
      } finally {
        busy = false;
      }
    };

    const createNameButton = (icon: HTMLElement, label: string, onClick: () => void) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'files-table__name-button';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'files-table__name-label';
      fillHighlightedText(nameSpan, label, searchQuery);
      button.append(icon, nameSpan);
      button.addEventListener('click', onClick);
      return button;
    };

    const createActionsCell = (
      visible: Array<{ labelKey: string; onClick: () => void }>,
      onMenu: (rect: DOMRect) => void,
    ) => {
      const actionsCell = document.createElement('td');
      actionsCell.className = 'data-table__actions';
      const wrap = document.createElement('div');
      wrap.className = 'data-table__actions-inner';
      for (const item of visible) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button ghost files-table__action-button';
        button.textContent = t(item.labelKey);
        button.addEventListener('click', item.onClick);
        wrap.append(button);
      }
      const menuButton = document.createElement('button');
      menuButton.type = 'button';
      menuButton.className = 'icon-button files-table__menu-button';
      menuButton.setAttribute('aria-label', t('files.owner.actions'));
      menuButton.textContent = '⋯';
      menuButton.addEventListener('click', () => onMenu(menuButton.getBoundingClientRect()));
      wrap.append(menuButton);
      actionsCell.append(wrap);
      return actionsCell;
    };

    for (const directory of directories) {
      const row = document.createElement('tr');
      if (searchHitEntryKey === directory.path) {
        row.classList.add('files-table__row--search-hit');
      }
      row.dataset.entryKey = directory.path;
      row.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        openDirectoryMenu(directory, new DOMRect(event.clientX, event.clientY, 0, 0));
      });
      const nameCell = document.createElement('td');
      nameCell.append(createNameButton(
        fileTypeImg('folder', 16),
        entryDisplayName(directory.path, currentPath),
        () => {
          setCurrentPath(directory.path);
          renderFolderContents(permission, currentManifest);
        },
      ));
      const typeCell = document.createElement('td');
      typeCell.className = 'files-table__type';
      typeCell.textContent = t('files.owner.folderType');
      const sizeCell = document.createElement('td');
      sizeCell.textContent = '—';
      row.append(
        nameCell,
        typeCell,
        sizeCell,
        createActionsCell(
          [{
            labelKey: 'files.owner.open',
            onClick: () => {
              setCurrentPath(directory.path);
              renderFolderContents(permission, currentManifest);
            },
          }],
          (rect) => openDirectoryMenu(directory, rect),
        ),
      );
      tbody.append(row);
    }

    for (const file of files) {
      const row = document.createElement('tr');
      if (searchHitEntryKey === file.path) {
        row.classList.add('files-table__row--search-hit');
      }
      row.dataset.entryKey = file.path;
      row.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        openFileMenu(file, new DOMRect(event.clientX, event.clientY, 0, 0));
      });
      const fileName = entryDisplayName(file.path, currentPath);
      const nameCell = document.createElement('td');
      nameCell.append(createNameButton(
        fileTypeImg(fileTypeIconFor(fileName, file.contentType ?? inferContentTypeFromFileName(fileName)), 16),
        fileName,
        () => { void downloadGuestFile(file); },
      ));
      const typeCell = document.createElement('td');
      typeCell.className = 'files-table__type';
      typeCell.textContent = t(fileTypeLabelKeyFor(fileName, file.contentType ?? inferContentTypeFromFileName(fileName)));
      const sizeCell = document.createElement('td');
      sizeCell.textContent = formatFileSize(file.size);
      const visibleActions = [
        ...(canEditGuestFile(file, permission)
          ? [{ labelKey: 'files.owner.edit', onClick: () => { void editGuestFile(file); } }]
          : []),
        { labelKey: 'files.owner.download', onClick: () => { void downloadGuestFile(file); } },
      ];
      row.append(
        nameCell,
        typeCell,
        sizeCell,
        createActionsCell(visibleActions, (rect) => openFileMenu(file, rect)),
      );
      tbody.append(row);
    }

    emptyEl.hidden = directories.length > 0 || files.length > 0;
    if (searchHitEntryKey) {
      const hitRow = tbody.querySelector(`tr[data-entry-key="${CSS.escape(searchHitEntryKey)}"]`);
      hitRow?.scrollIntoView?.({ block: 'nearest' });
    }

    if (pendingConflictMessage) {
      showConflict(pendingConflictMessage);
    }

    const guestTable = contentEl.querySelector('table.data-table') as HTMLTableElement | null;
    if (guestTable) {
      enhanceDataTableCards(guestTable);
    }

    const uploadInput = contentEl.querySelector('#files-guest-upload') as HTMLInputElement | null;
    uploadInput?.addEventListener('change', () => {
      const files = Array.from(uploadInput.files ?? []);
      uploadInput.value = '';
      if (files.length === 0) {
        return;
      }
      void uploadGuestFiles(files, currentPath);
    });

    const splitEl = contentEl.querySelector('.files-browse-split') as HTMLElement | null;
    if (splitEl) {
      bindFilesDropUpload({
        root: splitEl,
        getCurrentPath: () => currentPath,
        canUpload: () => guestCanUpload({ permission, ...getBudgetState() }),
        isBusy: () => uploading || busy,
        onDrop: (dropped, targetPath) => {
          void uploadGuestFiles(dropped, targetPath);
        },
        onRejectedEmpty: () => {
          showError('files.drop.emptyRejected');
        },
      });
    }

    const newFolderButton = contentEl.querySelector('#files-guest-new-folder') as HTMLButtonElement | null;
    const newTextButton = contentEl.querySelector('#files-guest-new-text') as HTMLButtonElement | null;
    const addButton = contentEl.querySelector('#files-guest-add') as HTMLButtonElement | null;
    addButton?.addEventListener('click', () => {
      openFilesContextMenu({
        anchorRect: addButton.getBoundingClientRect(),
        items: [
          ...(newFolderButton
            ? [{ id: 'new-folder', labelKey: 'files.owner.newFolder', action: () => newFolderButton.click() }]
            : []),
          ...(newTextButton
            ? [{ id: 'new-text', labelKey: 'files.owner.newTextFile', action: () => newTextButton.click() }]
            : []),
          ...(uploadInput
            ? [{ id: 'upload', labelKey: 'files.guest.upload', action: () => uploadInput.click() }]
            : []),
        ],
      });
    });

    newFolderButton?.addEventListener('click', () => {
      if (!folderKey || busy || !gateInfo) {
        return;
      }
      void showInputDialog({
        titleKey: 'files.owner.newFolderTitle',
        labelKey: 'files.owner.newFolderName',
        confirmLabelKey: 'files.owner.newFolderCreate',
        maxLength: 200,
        validate: (folderName) => entryNameConflict(currentManifest, currentPath, folderName),
        submit: async (folderName) => {
          if (!folderKey || !gateInfo) {
            throwSharedFolderInputError(new Error('status.error'));
          }
          busy = true;
          try {
            const result = await createSharedFolderGuestSubfolder({
              folderToken: route.folderToken,
              guestInviteId: route.guestInviteId,
              folderKey,
              revision: manifestRevision,
              manifest: currentManifest,
              currentPath,
              folderName,
              actor: guestActor,
            });
            manifestRevision = result.revision;
            currentManifest = result.manifest;
            clearConflict();
            noteConflicts(result.conflicts);
            renderFolderContents(gateInfo.permission, result.manifest);
          } catch (cause) {
            throwSharedFolderInputError(cause);
          } finally {
            busy = false;
          }
        },
      });
    });

    newTextButton?.addEventListener('click', () => {
      if (!folderKey || busy || !gateInfo) {
        return;
      }
      void showInputDialog({
        titleKey: 'files.owner.newTextFileTitle',
        labelKey: 'files.owner.newTextFileName',
        confirmLabelKey: 'files.owner.newTextFileCreate',
        defaultValue: 'notes.txt',
        maxLength: 200,
        validate: (rawName) => {
          try {
            return entryNameConflict(currentManifest, currentPath, ensureTextFileExtension(rawName));
          } catch {
            return t('files.owner.invalidName');
          }
        },
        submit: async (rawName) => {
          if (!folderKey || !gateInfo) {
            throwSharedFolderInputError(new Error('status.error'));
          }
          const fileName = ensureTextFileExtension(rawName);
          busy = true;
          try {
            const result = await uploadFileToSharedFolderAsGuest({
              folderToken: route.folderToken,
              guestInviteId: route.guestInviteId,
              folderKey,
              revision: manifestRevision,
              manifest: currentManifest,
              currentPath,
              file: new File([], fileName, { type: 'text/plain' }),
              actor: guestActor,
            });
            manifestRevision = result.revision;
            currentManifest = result.manifest;
            updateBudgetFromCommit(result.guestUploadFilesUsed, result.guestUploadBytesUsed);
            clearConflict();
            noteConflicts(result.conflicts);
            const path = joinManifestPath(currentPath, fileName);
            const created = result.manifest.entries.find(
              (entry): entry is SharedFolderFileEntry => entry.type === 'file' && entry.path === path,
            );
            // Conflict rename may have changed the final path — prefer the resolved entry.
            const resolvedPath = result.conflicts[0]?.resolvedPath ?? path;
            const resolved = result.manifest.entries.find(
              (entry): entry is SharedFolderFileEntry => entry.type === 'file' && entry.path === resolvedPath,
            ) ?? created;
            renderFolderContents(gateInfo.permission, result.manifest);
            if (resolved) {
              openGuestTextEditor(resolved, '');
            }
          } catch (cause) {
            throwSharedFolderInputError(cause);
          } finally {
            busy = false;
          }
        },
      });
    });
  };

  const reloadGuestManifestIfNewer = async (incomingRevision: number) => {
    if (
      !folderKey
      || !gateInfo
      || !folderId
      || opening
      || uploading
      || busy
      || incomingRevision <= manifestRevision
    ) {
      return;
    }

    syncingManifest = true;
    renderFolderContents(gateInfo.permission, currentManifest);
    try {
      const manifestBlob = await api.fetchSharedFolderGuestManifest(
        route.folderToken,
        route.guestInviteId,
      );
      const manifest = await decryptFolderManifest(folderKey, manifestBlob);
      if (incomingRevision > manifestRevision) {
        manifestRevision = incomingRevision;
        currentManifest = manifest;
      }
    } catch {
      // keep current view on transient errors
    } finally {
      syncingManifest = false;
      if (!disposed && gateInfo) {
        renderFolderContents(gateInfo.permission, currentManifest);
      }
    }
    void syncOpenTextEditor();
  };

  const openFolder = async () => {
    if (disposed || opening || opened || !gateInfo) {
      return;
    }

    const generation = ++mountGeneration;
    opening = true;
    errorEl.hidden = true;
    passwordField.hidden = true;
    setCryptoBusy(panel, busyHost, true, {
      titleKey: 'files.guest.opening',
      detailKey: 'files.guest.openingDetail',
      icon: 'hourglass',
    });

    try {
      const guestWrapKey = await resolveGuestWrapKey(
        fragment,
        passwordRequired ? passwordInput.value : undefined,
      );
      if (!guestWrapKey) {
        if (disposed || generation !== mountGeneration) {
          return;
        }
        passwordField.hidden = false;
        passwordInput.focus();
        showError('files.guest.passwordRequired');
        return;
      }

      // Refresh gate immediately before signing so the nonce cannot be
      // invalidated by a concurrent mount/remount race.
      const refreshed = await refreshGuestGate();
      if (disposed || generation !== mountGeneration) {
        return;
      }
      gateResponseText = refreshed.gateResponseText;
      gateInfo = refreshed.gateInfo;

      if (gateInfo.expired || gateInfo.revoked) {
        statusKey = gateInfo.revoked ? 'files.guest.revoked' : 'files.guest.expired';
        statusEl.hidden = false;
        statusEl.textContent = t(statusKey);
        return;
      }

      const signature = await signGateChallenge(proofSecretKey, gateInfo.nonce, gateResponseText);
      await api.verifySharedFolderGuest(
        route.folderToken,
        route.guestInviteId,
        signature,
        passwordRequired ? passwordInput.value : undefined,
      );
      if (disposed || generation !== mountGeneration) {
        return;
      }

      const envelopeMeta = await api.fetchSharedFolderKeyEnvelope(
        route.folderToken,
        route.guestInviteId,
      );
      const envelopeBlob = await api.fetchBlob(envelopeMeta.envelopeBlobId);
      folderKey = await decryptFolderGuestEnvelope(envelopeBlob, guestWrapKey);
      const manifestBlob = await api.fetchSharedFolderGuestManifest(
        route.folderToken,
        route.guestInviteId,
      );
      const manifest = await decryptFolderManifest(folderKey, manifestBlob);
      const manifestMeta = await api.getSharedFolderGuestManifestMeta(
        route.folderToken,
        route.guestInviteId,
      );
      if (disposed || generation !== mountGeneration) {
        return;
      }
      folderId = gateInfo.folderId;
      manifestRevision = manifestMeta.revision;
      currentManifest = manifest;

      opened = true;
      syncGuestGateChrome();
      renderFolderContents(gateInfo.permission, manifest);
      startExpiryMonitor(gateInfo.expiresAt);

      if (folderId) {
        connectSharedFolderLive(folderId, {
          onManifestRevision: (incomingRevision) => {
            void reloadGuestManifestIfNewer(incomingRevision);
          },
          onAccessEnded: ({ inviteId: endedInviteId }) => {
            if (endedInviteId && endedInviteId !== route.guestInviteId) {
              return;
            }
            void endGuestAccess('files.guest.accessRevoked');
          },
        });
      }
    } catch (cause) {
      if (disposed || generation !== mountGeneration) {
        return;
      }
      console.error('Guest folder open failed', cause);
      if (passwordRequired) {
        passwordField.hidden = false;
        passwordInput.focus();
      }

      const throttleKey = guestVerifyThrottleErrorKey(cause);
      if (throttleKey) {
        errorKey = throttleKey;
        errorEl.hidden = false;
        errorEl.textContent = apiErrorMessage(cause);
        statusEl.hidden = true;
      } else {
        const incorrectPassword =
          cause instanceof ApiError
          && (cause.code === 'incorrect_password'
            || cause.code === 'invalid_signature');
        const nonceExpired =
          cause instanceof ApiError && cause.code === 'nonce_expired';
        showError(
          nonceExpired
            ? 'info.guest.nonceExpired'
            : incorrectPassword
              ? 'info.guest.incorrectPassword'
              : 'info.guest.unsealFailed',
        );
      }
    } finally {
      if (!disposed && generation === mountGeneration) {
        setCryptoBusy(panel, busyHost, false, {
          titleKey: 'files.guest.opening',
          detailKey: 'files.guest.openingDetail',
        });
        opening = false;
      }
    }
  };

  openButton.addEventListener('click', () => {
    void openFolder();
  });

  passwordInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void openFolder();
    }
  });

  void (async () => {
    const generation = mountGeneration;
    try {
      const refreshed = await refreshGuestGate();
      if (disposed || generation !== mountGeneration) {
        return;
      }
      gateResponseText = refreshed.gateResponseText;
      gateInfo = refreshed.gateInfo;

      if (gateInfo.expired || gateInfo.revoked) {
        statusKey = gateInfo.revoked ? 'files.guest.revoked' : 'files.guest.expired';
        statusEl.hidden = false;
        statusEl.textContent = t(statusKey);
        return;
      }

      if (gateInfo.passwordRequired) {
        passwordRequired = true;
        passwordField.hidden = false;
        openButton.hidden = false;
        passwordInput.required = true;
        passwordInput.focus();
        return;
      }

      await openFolder();
    } catch (cause) {
      if (disposed || generation !== mountGeneration) {
        return;
      }
      console.error('Guest folder gate failed', cause);
      const throttleKey = guestVerifyThrottleErrorKey(cause);
      if (throttleKey) {
        errorKey = throttleKey;
        errorEl.hidden = false;
        errorEl.textContent = apiErrorMessage(cause);
        statusEl.hidden = true;
        return;
      }
      showError('files.guest.invalidLink');
    }
  })();

  return panel;
}
